/**
 * Custom hook for Contagem modal state management
 *
 * Extracts modal-related state from ContagemCompletaPage to reduce component complexity.
 * Manages visibility of all modals used in the page.
 *
 * Usage:
 * const { showReportModal, openReportModal, closeReportModal, ... } = useContagemModals();
 */
import { useState, useCallback } from 'react';

export interface UseContagemModalsReturn {
  // Report Modal
  showReportModal: boolean;
  openReportModal: () => void;
  closeReportModal: () => void;

  // Import Modal (Itens)
  showImportModal: boolean;
  openImportModal: () => void;
  closeImportModal: () => void;

  // Cliente Modal
  showClienteModal: boolean;
  openClienteModal: () => void;
  closeClienteModal: () => void;

  // Import Informacoes Modal
  showImportInformacoesModal: boolean;
  openImportInformacoesModal: () => void;
  closeImportInformacoesModal: () => void;

  // Info Informacoes Modal
  showInfoInformacoesModal: boolean;
  openInfoInformacoesModal: () => void;
  closeInfoInformacoesModal: () => void;

  // Info Itens Modal
  showInfoItensModal: boolean;
  openInfoItensModal: () => void;
  closeInfoItensModal: () => void;

  // Utility
  closeAllModals: () => void;
}

export const useContagemModals = (): UseContagemModalsReturn => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showImportInformacoesModal, setShowImportInformacoesModal] = useState(false);
  const [showInfoInformacoesModal, setShowInfoInformacoesModal] = useState(false);
  const [showInfoItensModal, setShowInfoItensModal] = useState(false);

  // Report Modal
  const openReportModal = useCallback(() => setShowReportModal(true), []);
  const closeReportModal = useCallback(() => setShowReportModal(false), []);

  // Import Modal
  const openImportModal = useCallback(() => setShowImportModal(true), []);
  const closeImportModal = useCallback(() => setShowImportModal(false), []);

  // Cliente Modal
  const openClienteModal = useCallback(() => setShowClienteModal(true), []);
  const closeClienteModal = useCallback(() => setShowClienteModal(false), []);

  // Import Informacoes Modal
  const openImportInformacoesModal = useCallback(() => setShowImportInformacoesModal(true), []);
  const closeImportInformacoesModal = useCallback(() => setShowImportInformacoesModal(false), []);

  // Info Informacoes Modal
  const openInfoInformacoesModal = useCallback(() => setShowInfoInformacoesModal(true), []);
  const closeInfoInformacoesModal = useCallback(() => setShowInfoInformacoesModal(false), []);

  // Info Itens Modal
  const openInfoItensModal = useCallback(() => setShowInfoItensModal(true), []);
  const closeInfoItensModal = useCallback(() => setShowInfoItensModal(false), []);

  // Utility: Close all modals
  const closeAllModals = useCallback(() => {
    setShowReportModal(false);
    setShowImportModal(false);
    setShowClienteModal(false);
    setShowImportInformacoesModal(false);
    setShowInfoInformacoesModal(false);
    setShowInfoItensModal(false);
  }, []);

  return {
    // Report Modal
    showReportModal,
    openReportModal,
    closeReportModal,

    // Import Modal
    showImportModal,
    openImportModal,
    closeImportModal,

    // Cliente Modal
    showClienteModal,
    openClienteModal,
    closeClienteModal,

    // Import Informacoes Modal
    showImportInformacoesModal,
    openImportInformacoesModal,
    closeImportInformacoesModal,

    // Info Informacoes Modal
    showInfoInformacoesModal,
    openInfoInformacoesModal,
    closeInfoInformacoesModal,

    // Info Itens Modal
    showInfoItensModal,
    openInfoItensModal,
    closeInfoItensModal,

    // Utility
    closeAllModals,
  };
};

export default useContagemModals;
